/**
 * HowWeWorkSection — section 6 of the homepage (Konzept § 5 Z. 131).
 *
 * Job: differentiate. This is the first place on the page where the term
 * "Agentic Development" is allowed to appear — the hero deliberately stays in
 * customer language ("mit KI-Agenten entwickeln"), per Fundament § 9.3 Schicht 2.
 *
 * The CTA is optional: /agentic-development is its own Phase-3 stage and does not
 * exist yet, so the section ships without an exit rather than with a dead link.
 * The screencast slot (image) is likewise optional and must not block the launch
 * (Konzept Z. 296).
 */

import React from "react";
import { type SectionHowWeWorkProps } from "../../content/section-how-we-work";
import { ResponsiveImage, SectionTextLink } from "../ui";
import { useLocalizedUrl } from "../../hooks";

export type HowWeWorkSectionProps = SectionHowWeWorkProps & {
    /**
     * Teil eines Kapitels statt eigenstaendige Sektion? (12.8.2026)
     *
     * Im Kapitel «Unser Ansatz» folgt dieser Teil auf den Absatz, der sagt WAS
     * wir bauen — er sagt WIE. Die Kapitelueberschrift steht als `h2`, «So
     * arbeiten wir» faellt deshalb auf `h3`.
     *
     * Der eigene Ausgang bleibt: ein Kapitel mit zwei Ausgaengen ist zulaessig,
     * ein verlorener Ausgang waere ein Fehler.
     */
    nested?: boolean;
};

export const HowWeWorkSection: React.FC<HowWeWorkSectionProps> = ({
    title,
    description,
    ctaText,
    ctaHref,
    image,
    imageAlt,
    nested = false,
}) => {
    // Vor jedem bedingten Ausstieg — sonst laufen mit und ohne CTA
    // verschiedene Hooks (Rules of Hooks).
    const localizedCtaHref = useLocalizedUrl(ctaHref ?? "/agentic-development");
    const Heading = nested ? "h3" : "h2";

    return (
        <section data-testid="how-we-work-section" className={nested ? "" : "bg-white py-24"}>
            <div className="mx-auto w-full px-4 md:px-12">
                {/*
                    Zwei Spalten nur MIT Bild. Ohne Bild reservierte das feste
                    `lg:grid-cols-2` die zweite Spalte trotzdem und presste den
                    Text auf die halbe Breite — schmaler als die Sektionen
                    darueber (Jan, 5.8.2026).
                */}
                <div className={`grid items-center gap-12 ${image ? "lg:grid-cols-2" : ""}`}>
                    {/*
                        Ausrichtung folgt dem Layout (Jan, 6.8.2026), wie im
                        `AboutTeaserSection`: MIT Bild steht der Text linksbuendig
                        daneben. OHNE Bild faellt das Raster weg, der Block laeuft
                        ueber die volle Breite — und linksbuendiger Text sieht dann
                        neben den zentrierten Nachbarsektionen falsch aus.
                    */}
                    <div className={image ? "" : "mx-auto max-w-3xl text-center"}>
                        <Heading
                            className={`mb-6 font-black text-gray-900 ${
                                nested ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"
                            }`}
                        >
                            {title}
                        </Heading>
                        <p className="mb-8 text-lg leading-relaxed text-gray-600">{description}</p>
                        {/*
                        Textlink statt Knopf (Jan, 3.8.2026): der Ausgang fuehrt
                        auf eine Methodenseite, nicht in eine Handlung. Knoepfe
                        bleiben dem Abschluss-Block und den Karten.

                        `flex justify-center` ohne Bild: `SectionTextLink` ist
                        `inline-flex` und richtet sich nicht nach `text-align`.
                    */}
                        {ctaText && ctaHref && (
                            <div className={image ? "" : "flex justify-center"}>
                                <SectionTextLink to={localizedCtaHref}>{ctaText}</SectionTextLink>
                            </div>
                        )}
                    </div>
                    {image && (
                        <div>
                            <ResponsiveImage
                                src={image}
                                alt={imageAlt || ""}
                                className="h-auto w-full rounded-2xl"
                                widths={[640, 960, 1280]}
                                sizes="(max-width: 1024px) 100vw, 50vw"
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default HowWeWorkSection;
